import {
    Button,
    Dialog as MuiDialog,
    DialogActions,
    DialogActionsProps,
    DialogContent,
    DialogContentProps,
    DialogProps,
    DialogTitle,
    DialogTitleProps,
    IconButtonProps,
    SvgIconTypeMap,
    ButtonProps,
    Typography,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { ReactNode } from 'react';

import DialogCloseButton from './components';

export interface DialogAction {
    label: string;
    /**
     * Optional because `form` is the other way to trigger an action: a button that submits a form
     * by id needs no handler, and both call sites here already tolerate its absence — the negative
     * action falls back to `onClose`.
     */
    onClick?: () => void | Promise<void>;
    disabled?: boolean;
    testId?: string;
    loading?: boolean;
    variant?: ButtonProps['variant'];
    /** Defaults to MUI's own `primary`. `neutral` is the grey the theme registers for a resting action. */
    color?: ButtonProps['color'];
    icon?: OverridableComponent<SvgIconTypeMap<object, 'svg'>> & {
        muiName: string;
    };
    endIcon?: ReactNode;
    form?: string;
    fullWidth?: boolean;
}

interface HubDialogProps extends DialogProps {
    open: boolean;
    heading?: string | ReactNode;
    negativeAction?: DialogAction;
    positiveAction?: DialogAction;
    actionButtonsSize?: ButtonProps['size'];
    actions?: ReactNode;
    onClose?: () => void;
    children?: ReactNode;
    showDialogActions?: boolean;
    /**
     * Show the header's close button (the X). On by default, so `onClose` alone still renders it.
     *
     * Named for the position, not the action: the footer's own exit is a close too, and a name
     * that did not say *which* close read as though it governed both.
     *
     * Turning it off keeps `onClose` wired to the backdrop and Escape — this hides the control,
     * it does not make the dialog undismissable. For a surface whose footer already carries the
     * only exit it wants to offer.
     */
    showHeaderCloseButton?: boolean;
    textContent?: string;
    /** Content rendered between the heading and dialog body, flush with the header border */
    banner?: ReactNode;
    componentProps?: {
        dialogTitle?: DialogTitleProps;
        dialogContent?: DialogContentProps;
        dialogActions?: DialogActionsProps;
        dialogCloseButton?: IconButtonProps;
    };
}

function Dialog({
    open,
    heading,
    negativeAction,
    positiveAction,
    actionButtonsSize,
    onClose,
    children,
    showDialogActions = true,
    showHeaderCloseButton = true,
    actions,
    banner,
    componentProps = {
        dialogTitle: {},
        dialogContent: {},
        dialogActions: {},
        dialogCloseButton: {},
    },
    ...props
}: Readonly<HubDialogProps>) {
    return (
        <MuiDialog data-testid="dialog" open={open} onClose={onClose} aria-labelledby="dialog" {...props}>
            {!!heading && (
                <DialogTitle
                    data-testid="dialog-heading"
                    component="div"
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 2.5,
                    }}
                    {...componentProps.dialogTitle}>
                    <Typography variant="h3">{heading}</Typography>
                    {onClose && showHeaderCloseButton && (
                        <DialogCloseButton
                            onClick={onClose}
                            data-testid="dialog-close-button"
                            sx={{
                                backgroundColor: 'cement.100',
                                borderRadius: '50%',
                                position: 'relative',
                                top: 'auto',
                                right: 'auto',
                                mr: -1,
                            }}
                            {...componentProps.dialogCloseButton}
                        />
                    )}
                </DialogTitle>
            )}

            {banner}
            <DialogContent {...componentProps.dialogContent}>{children}</DialogContent>
            {!actions && showDialogActions && (negativeAction || positiveAction) && (
                <DialogActions {...componentProps.dialogActions}>
                    {negativeAction && (
                        <Button
                            data-testid={negativeAction?.testId}
                            variant={negativeAction?.variant ?? 'outlined'}
                            color={negativeAction?.color}
                            size={actionButtonsSize ?? 'large'}
                            loading={negativeAction?.loading}
                            disabled={negativeAction?.disabled}
                            fullWidth={negativeAction?.fullWidth}
                            onClick={negativeAction?.onClick ?? onClose}>
                            {negativeAction?.label ?? 'Cancel'}
                        </Button>
                    )}
                    {positiveAction && (
                        <Button
                            data-testid={positiveAction?.testId}
                            variant={positiveAction?.variant ?? 'contained'}
                            color={positiveAction?.color}
                            size={actionButtonsSize ?? 'large'}
                            onClick={positiveAction?.onClick}
                            form={positiveAction?.form ?? undefined}
                            type={positiveAction?.form ? 'submit' : 'button'}
                            disabled={positiveAction?.disabled}
                            loading={positiveAction?.loading}
                            fullWidth={positiveAction?.fullWidth}
                            endIcon={positiveAction?.endIcon}>
                            {positiveAction?.label ?? 'Submit'}
                        </Button>
                    )}
                </DialogActions>
            )}
            {actions && (
                <DialogActions
                    {...componentProps.dialogActions}
                    sx={[
                        { width: '100%' },
                        ...(Array.isArray(componentProps.dialogActions?.sx)
                            ? componentProps.dialogActions.sx
                            : [componentProps.dialogActions?.sx]),
                    ]}>
                    {actions}
                </DialogActions>
            )}
        </MuiDialog>
    );
}

export default Dialog;
